import isAfter from "date-fns/isAfter";
import type { JSX } from "react";
import * as React from "react";
import { NavigateFunction } from "react-router";
import PrintButton from "../../components/atoms/PrintButton";
import { LearningMaterialTagAnalyticsSwitcher } from "../../components/features/LearningMaterialTagAnalyticsSwitcher";
import { StudentAnalyticsTypeTab } from "../../components/features/StudentAnalyticsTypeTab";
import { DateRangeNavigation } from "../../components/general/DateRangeNavigation";
import StudentAnalyticsGraph from "../../components/molecules/StudentAnalyticsGraph";
import StudentAnalyticsTable from "../../components/molecules/StudentAnalyticsTable";
import SwitchTabs from "../../components/molecules/SwitchTabs";
import { hasLearningManagementService } from "../../domains/PaidService";
import { isPushableCalendarNext } from "../../helpers/AnalyticsHelper";
import { AnalyticsFilter } from "../../hooks/filters/useAnalyticsFilter";
import {
  AnalyticsTermType,
  StudentAnalyticsType,
} from "../../interfaces/AnalyticsTableInterface";
import { OrderDirFilterType } from "../../interfaces/FiltersInterface";
import StudentAnalyticsTableInterface, {
  StudentAnalyticsRowInterface,
} from "../../interfaces/StudentAnalyticsTableInterface";
import StudentInterface from "../../interfaces/StudentInterface";
import AnalyticsShareButton from "./AnalyticsShareButton";
import styles from "./styles.scss";
import { TotalTime } from "./TotalTime";

interface Props {
  student: StudentInterface;
  isLoading: boolean;
  isLoadingNextPage: boolean;
  analyticsTable: StudentAnalyticsTableInterface | undefined;
  analyticsType: Extract<StudentAnalyticsType, "time" | "amount">;
  navigate: NavigateFunction;
  analyticsFilter: AnalyticsFilter;
  onDateFilterChange: (dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  }) => void;
  onTermChange: (term: AnalyticsTermType) => void;
  onOrderChange: (order: string | null, orderDir: OrderDirFilterType) => void;
  hasMoreData: boolean;
}

interface State {
  uncheckedRows: StudentAnalyticsRowInterface[];
}

export default class StudentAnalytics extends React.Component<Props, State> {
  get checkedRows(): StudentAnalyticsRowInterface[] {
    const { uncheckedRows } = this.state;

    if (uncheckedRows && this.props.analyticsTable) {
      return this.props.analyticsTable.learningMaterials.filter(
        (learningMaterial) => {
          return !uncheckedRows.some((row) => row.id === learningMaterial.id);
        },
      );
    } else {
      return [];
    }
  }

  constructor(props: Props) {
    super(props);
    this.state = {
      uncheckedRows: [],
    };
  }

  render(): JSX.Element {
    return (
      <React.Fragment>
        <div className={styles.tab}>
          <StudentAnalyticsTypeTab
            activeAnalyticsType={this.props.analyticsType}
            student={this.props.student}
          />
        </div>
        <div className={styles.tableControl}>
          <div className={styles.MaterialSwitchWrapper}>
            <LearningMaterialTagAnalyticsSwitcher
              student={this.props.student}
              currentAnalyticsType={this.props.analyticsType}
              value="material"
            />
          </div>
          <DateRangeNavigation
            startDate={this.props.analyticsFilter.startDate}
            endDate={this.props.analyticsFilter.endDate}
            term={this.props.analyticsFilter.term}
            onDatesChange={this.handleDatesChange}
            calendarDisabled={!this.isDaily()}
            canPushRightButton={isPushableCalendarNext(
              this.props.analyticsFilter.endDate,
              this.props.analyticsFilter.term,
            )}
            isDayBlocked={(day: Date) => isAfter(day, new Date())}
          />
          <div className={styles.tableControlRight}>
            <AnalyticsShareButton
              student={this.props.student}
              learningMaterials={this.checkedRows}
              startDate={this.props.analyticsFilter.startDate}
              endDate={this.props.analyticsFilter.endDate}
              term={this.props.analyticsFilter.term}
              analyticsType={this.props.analyticsType}
              columns={this.props.analyticsTable?.columns ?? []}
              totalScore={this.props.analyticsTable?.totalScore ?? 0}
            />
            <SwitchTabs
              tabs={[
                {
                  id: "daily",
                  label: "日",
                  active: this.isDaily(),
                },
                {
                  id: "weekly",
                  label: "週",
                  active: this.props.analyticsFilter.term === "weekly",
                },
                {
                  id: "monthly",
                  label: "月",
                  active: this.props.analyticsFilter.term === "monthly",
                },
              ]}
              onClickTab={(tab: any) => () => {
                this.props.onTermChange(tab.id);
              }}
            />
            <PrintButton />
          </div>
        </div>
        <div className={styles.tableWrapper}>
          <div className="px-8 pb-8 pt-7">
            {this.props.hasMoreData && (
              <div className="float-left text-sm text-gray-900">
                ※全件表示されていません。画面をスクロールして全ての学習記録を表示できます
              </div>
            )}
            {this.props.analyticsTable &&
              this.props.analyticsType === "time" && (
                <TotalTime
                  learningMaterials={this.checkedRows}
                  withFreePlanTooltip={
                    !hasLearningManagementService(this.props.student)
                  }
                />
              )}
            {this.props.analyticsTable && (
              <StudentAnalyticsGraph
                learningMaterials={this.checkedRows}
                term={this.props.analyticsFilter.term}
                analyticsType={this.props.analyticsType}
                columns={this.props.analyticsTable.columns}
              />
            )}
          </div>
          <StudentAnalyticsTable
            student={this.props.student}
            isLoading={this.props.isLoading}
            isLoadingNextPage={this.props.isLoadingNextPage}
            analyticsTable={this.props.analyticsTable}
            analyticsType={this.props.analyticsType}
            orderDir={this.props.analyticsFilter.orderDir}
            order={this.props.analyticsFilter.order}
            onOrderChange={this.props.onOrderChange}
            onHeadCheckboxChange={this.handleHeadCheckboxChange}
            onRowCheckboxChange={this.handleRowCheckboxChange}
            uncheckedRows={this.state.uncheckedRows}
          />
        </div>
      </React.Fragment>
    );
  }

  private isUncheckedRow(row: StudentAnalyticsRowInterface): boolean {
    return this.state.uncheckedRows.some(
      (uncheckedRow) => uncheckedRow.id === row.id,
    );
  }

  private handleHeadCheckboxChange = () => {
    if (!this.props.analyticsTable || !this.state.uncheckedRows) {
      return;
    }
    if (
      this.state.uncheckedRows.length <
      this.props.analyticsTable.learningMaterials.length
    ) {
      this.setState({
        uncheckedRows: this.props.analyticsTable.learningMaterials,
      });
    } else {
      this.setState({
        uncheckedRows: [],
      });
    }
  };

  private handleRowCheckboxChange =
    (row: StudentAnalyticsRowInterface) => () => {
      if (this.isUncheckedRow(row)) {
        this.setState({
          uncheckedRows: this.state.uncheckedRows.filter(
            (uncheckedRow) => uncheckedRow.id !== row.id,
          ),
        });
      } else {
        this.setState({
          uncheckedRows: this.state.uncheckedRows.concat(row),
        });
      }
    };

  private isDaily(): boolean {
    return (
      !this.props.analyticsFilter.term ||
      this.props.analyticsFilter.term === "daily"
    );
  }

  private handleDatesChange = (dates: { startDate?: Date; endDate?: Date }) => {
    if (dates.startDate && dates.endDate) {
      this.props.onDateFilterChange({
        startDate: dates.startDate,
        endDate: dates.endDate,
      });
    }
  };
}
